import type { IAnalyzer, DiagramData } from '../../../core/ai/interfaces/IAnalyzer';
import type { IInsight } from '../../../core/ai/interfaces/IInsight';
import { createInsight } from '../../../core/ai/interfaces/IInsight';

const STEP_TYPES = new Set(['process', 'flowchart-shape', 'bpmn-shape']);
const DECISION_TYPES = new Set(['decision']);

/** SOC 2 Trust Service Criteria control codes. */
const SOC2_CONTROLS = [
  'CC1.1', 'CC1.2', 'CC1.3', 'CC1.4', 'CC1.5',
  'CC2.1', 'CC2.2', 'CC2.3',
  'CC3.1', 'CC3.2', 'CC3.3', 'CC3.4',
  'CC4.1', 'CC4.2',
  'CC5.1', 'CC5.2', 'CC5.3',
  'CC6.1', 'CC6.2', 'CC6.3', 'CC6.6', 'CC6.7', 'CC6.8',
  'CC7.1', 'CC7.2', 'CC7.3', 'CC7.4', 'CC7.5',
  'CC8.1',
  'CC9.1', 'CC9.2',
];

function getField(node: DiagramData['nodes'][0], field: string): unknown {
  return node.data?.[field];
}

function getLabel(node: DiagramData['nodes'][0]): string {
  const d = node.data ?? {};
  return (d.label ?? d.text ?? d.name ?? '') as string;
}

interface CheckResult {
  status: 'pass' | 'warn' | 'fail';
  message: string;
  affectedNodeIds: string[];
}

export class ComplianceChecker implements IAnalyzer {
  id = 'process-compliance';
  supportedDiagramTypes = ['flowchart'] as const;

  analyze(data: DiagramData): IInsight[] {
    const steps = data.nodes.filter((n) => STEP_TYPES.has(n.type));
    const decisions = data.nodes.filter((n) => DECISION_TYPES.has(n.type));
    const allWorkNodes = [...steps, ...decisions];

    if (allWorkNodes.length < 2) return [];

    // Check if any compliance data exists — if not, suggest enabling the pack
    const hasAnyComplianceData = allWorkNodes.some(
      (n) => ['controls', 'evidence', 'findings'].some((field) => {
        const value = getField(n, field);
        return Array.isArray(value) && value.length > 0;
      }),
    );

    if (!hasAnyComplianceData) {
      return [
        createInsight({
          id: 'compliance-pack-not-enabled',
          category: 'opportunity',
          priority: 'info',
          title: 'Add compliance data for audit review',
          description: 'No compliance data found. Enable compliance tracking in folder settings if needed, then map controls, evidence, and findings to process steps in Properties and run the review again. Ask a folder administrator or editor if you do not have access.',
          affectedNodeIds: [],
          isDismissible: true,
        }),
      ];
    }

    const checks: { name: string; result: CheckResult }[] = [
      { name: 'ownership', result: this.checkOwnership(allWorkNodes) },
      { name: 'duration', result: this.checkDurations(allWorkNodes) },
      { name: 'controls', result: this.checkControlCoverage(allWorkNodes) },
      { name: 'evidence', result: this.checkEvidenceCoverage(steps) },
      { name: 'findings', result: this.checkFindingsAddressed(allWorkNodes) },
      { name: 'dead-ends', result: this.checkDeadEnds(data) },
      { name: 'segregation', result: this.checkSegregationOfDuties(decisions, steps) },
    ];

    const passCount = checks.filter((c) => c.result.status === 'pass').length;
    const score = Math.round((passCount / checks.length) * 100);

    const insights: IInsight[] = [];

    // Overall score insight
    const overallStatus = score >= 80 ? 'pass' : score >= 50 ? 'warn' : 'fail';
    insights.push(
      createInsight({
        id: 'compliance-overall',
        category: 'risk',
        priority: overallStatus === 'fail' ? 'high' : overallStatus === 'warn' ? 'medium' : 'low',
        title: `SOC 2 readiness: ${score}% (${passCount}/${checks.length} checks pass)`,
        description: overallStatus === 'pass'
          ? 'This process meets baseline SOC 2 readiness criteria.'
          : `${checks.length - passCount} check${checks.length - passCount > 1 ? 's' : ''} need attention before this process is audit-ready.`,
        affectedNodeIds: [],
        isDismissible: true,
        relevanceScore: 10,
      }),
    );

    // Individual check insights for non-passing checks
    for (const check of checks) {
      if (check.result.status === 'pass') continue;
      insights.push(
        createInsight({
          id: `compliance-${check.name}`,
          category: 'risk',
          priority: check.result.status === 'fail' ? 'high' : 'medium',
          title: check.result.message,
          description: check.result.message,
          affectedNodeIds: check.result.affectedNodeIds,
          isDismissible: true,
        }),
      );
    }

    return insights;
  }

  private checkOwnership(nodes: DiagramData['nodes']): CheckResult {
    const noOwner = nodes.filter((n) => !getField(n, 'owner'));
    const pct = ((nodes.length - noOwner.length) / nodes.length) * 100;
    if (pct >= 100) return { status: 'pass', message: 'All steps have owners', affectedNodeIds: [] };
    if (pct >= 80) return { status: 'warn', message: `${noOwner.length} step(s) without owner (${Math.round(pct)}% assigned)`, affectedNodeIds: noOwner.map((n) => n.id) };
    return { status: 'fail', message: `${noOwner.length} step(s) without owner (${Math.round(pct)}% assigned)`, affectedNodeIds: noOwner.map((n) => n.id) };
  }

  private checkDurations(nodes: DiagramData['nodes']): CheckResult {
    const noDuration = nodes.filter((n) => !getField(n, 'duration'));
    const pct = ((nodes.length - noDuration.length) / nodes.length) * 100;
    if (pct >= 100) return { status: 'pass', message: 'All steps have durations', affectedNodeIds: [] };
    if (pct >= 80) return { status: 'warn', message: `${noDuration.length} step(s) without duration`, affectedNodeIds: noDuration.map((n) => n.id) };
    return { status: 'fail', message: `${noDuration.length} step(s) without duration`, affectedNodeIds: noDuration.map((n) => n.id) };
  }

  private checkControlCoverage(nodes: DiagramData['nodes']): CheckResult {
    const mappedControls = new Set<string>();
    const noControls: string[] = [];

    for (const node of nodes) {
      const controls = getField(node, 'controls') as string[] | undefined;
      if (controls && Array.isArray(controls) && controls.length > 0) {
        controls.forEach((c) => mappedControls.add(c));
      } else {
        noControls.push(node.id);
      }
    }

    if (mappedControls.size === 0) {
      return { status: 'fail', message: 'No controls mapped to any step', affectedNodeIds: noControls };
    }

    const coveredSoc2 = SOC2_CONTROLS.filter((c) => mappedControls.has(c));
    if (coveredSoc2.length >= SOC2_CONTROLS.length * 0.5) {
      return { status: 'pass', message: `${coveredSoc2.length} SOC 2 controls mapped`, affectedNodeIds: [] };
    }
    return { status: 'warn', message: `Only ${coveredSoc2.length} of ${SOC2_CONTROLS.length} SOC 2 controls mapped`, affectedNodeIds: noControls };
  }

  private checkEvidenceCoverage(steps: DiagramData['nodes']): CheckResult {
    const noEvidence = steps.filter((n) => {
      const evidence = getField(n, 'evidence') as unknown[] | undefined;
      return !evidence || !Array.isArray(evidence) || evidence.length === 0;
    });

    const pct = ((steps.length - noEvidence.length) / steps.length) * 100;
    if (pct >= 100) return { status: 'pass', message: 'All steps have evidence items', affectedNodeIds: [] };
    if (pct >= 80) return { status: 'warn', message: `${noEvidence.length} step(s) without evidence items`, affectedNodeIds: noEvidence.map((n) => n.id) };
    return { status: 'fail', message: `${noEvidence.length} step(s) without evidence items`, affectedNodeIds: noEvidence.map((n) => n.id) };
  }

  private checkFindingsAddressed(nodes: DiagramData['nodes']): CheckResult {
    const unaddressed: string[] = [];

    for (const node of nodes) {
      const findings = getField(node, 'findings') as Array<{ severity?: string; prevention?: string }> | undefined;
      if (!findings || !Array.isArray(findings)) continue;
      const criticalWithoutPrevention = findings.some(
        (f) => f.severity === 'critical' && (!f.prevention || !f.prevention.trim()),
      );
      if (criticalWithoutPrevention) unaddressed.push(node.id);
    }

    if (unaddressed.length === 0) return { status: 'pass', message: 'All critical findings have prevention', affectedNodeIds: [] };
    return { status: 'fail', message: `${unaddressed.length} step(s) have critical findings without prevention`, affectedNodeIds: unaddressed };
  }

  private checkDeadEnds(data: DiagramData): CheckResult {
    const outgoing = new Map<string, number>();
    const incoming = new Map<string, number>();
    for (const edge of data.edges) {
      outgoing.set(edge.from, (outgoing.get(edge.from) ?? 0) + 1);
      incoming.set(edge.to, (incoming.get(edge.to) ?? 0) + 1);
    }

    const deadEnds = data.nodes.filter(
      (n) => STEP_TYPES.has(n.type) && (outgoing.get(n.id) ?? 0) === 0 && (incoming.get(n.id) ?? 0) > 0,
    );

    if (deadEnds.length === 0) return { status: 'pass', message: 'All paths reach an end event', affectedNodeIds: [] };
    return {
      status: 'warn',
      message: `${deadEnds.length} dead-end step(s): ${deadEnds.map((n) => getLabel(n) || n.id).join(', ')}`,
      affectedNodeIds: deadEnds.map((n) => n.id),
    };
  }

  private checkSegregationOfDuties(
    decisions: DiagramData['nodes'],
    steps: DiagramData['nodes'],
  ): CheckResult {
    if (decisions.length === 0) return { status: 'pass', message: 'No decision points to evaluate', affectedNodeIds: [] };

    const roles = new Set<string>();
    for (const step of steps) {
      const owner = getField(step, 'owner') as string | undefined;
      if (owner) roles.add(owner);
    }

    if (roles.size < 2 && steps.length > 3) {
      return {
        status: 'warn',
        message: `Only ${roles.size} role${roles.size === 1 ? '' : 's'} assigned across ${steps.length} steps — consider segregation of duties`,
        affectedNodeIds: steps.map((n) => n.id),
      };
    }

    return { status: 'pass', message: 'Multiple roles assigned', affectedNodeIds: [] };
  }
}
